/**
 * Attendance Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as attendanceService from '../api/services/attendance.service';
import { toast } from '../stores/toast.store';

const KEYS = {
  today: ['attendance', 'today'] as const,
  history: (days: number) => ['attendance', 'history', days] as const,
};

/**
 * Hook for fetching today's attendance status
 */
export function useAttendanceToday() {
  return useQuery({
    queryKey: KEYS.today,
    queryFn: async () => {
      const result = await attendanceService.getAttendanceToday();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch attendance');
      }
      return result.data!;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Hook for fetching attendance history
 */
export function useAttendanceHistory(days = 7) {
  return useQuery({
    queryKey: KEYS.history(days),
    queryFn: async () => {
      const result = await attendanceService.getAttendanceHistory(days);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch history');
      }
      return result.data!.items;
    },
  });
}

/**
 * Hook for clocking in
 */
export function useClockIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await attendanceService.clockIn();
      if (!result.success) {
        throw new Error(result.error?.message || 'Clock in failed');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.today });
      toast.success(data.message);
    },
    onError: (error: Error) => {
      toast.error('打卡失敗', error.message);
    },
  });
}

/**
 * Hook for clocking out
 */
export function useClockOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await attendanceService.clockOut();
      if (!result.success) {
        throw new Error(result.error?.message || 'Clock out failed');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.today });
      toast.success(data.message);
    },
    onError: (error: Error) => {
      toast.error('打卡失敗', error.message);
    },
  });
}
